import { QueryParams } from '@/core/interfaces/common';

import {
  buildPrefillParams,
  sanitizePrefillParams,
} from '@/utils/helpers';
import {
  decryptJson,
  encryptJson,
} from '@/utils/prefillCrypto';

/*
 The mus_data query param carries the mus_* prefill params obfuscated, so
 contact details aren't readable in URLs (logs, caches, analytics). Both
 ends run in the browser: the lookup widget builds links as the visitor
 types, and V4 landing pages read them on load. See prefillCrypto for why
 this is obfuscation, not secrecy.
 */
export const encryptedPrefillParam = 'mus_data';

// Legitimate tokens for capped contact fields are well under 2 KB
const maxTokenLength = 4096;

/*
 Returns the query string for links that prefill a landing page with this
 contact, e.g. `mus_data=v1.…`. Returns '' when there is nothing to prefill
 or no key is configured; links then work without prefill. Never falls
 back to plaintext params.
 */
export const buildEncryptedPrefillQuery = async (
  contact: Parameters<typeof buildPrefillParams>[0]
): Promise<string> => {
  const params = buildPrefillParams(contact);

  if (!Object.keys(params).length) {
    return '';
  }

  try {
    const token = await encryptJson(params);

    return new URLSearchParams({ [encryptedPrefillParam]: token }).toString();
  } catch {
    return '';
  }
};

/*
 Decrypts a mus_data param into mus_* prefill params, keeping only known
 keys. Anyone can make a token, so its contents are untrusted input.
 Returns null for a missing, invalid or expired token.
 */
export const readEncryptedPrefill = async (
  token: QueryParams[string]
): Promise<QueryParams | null> => {
  if (typeof token !== 'string' || !token || token.length > maxTokenLength) {
    return null;
  }

  return sanitizePrefillParams(await decryptJson(token));
};
